import React, { useContext, useEffect, useRef, useState } from 'react';
import { useDebounce } from 'use-debounce';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import {
    Alert_,
    AlertContext,
    Checkbox_,
    Grey_Link,
    Input_,
    Main_Plain,
    PageAlwaysVisible,
    PageFull,
    PageSidebar,
    PageTitle,
    Partial_Loader,
    Table,
    TD,
    TH,
    Toggle_Stat,
    TR
} from 'monica-alexandria';
import store from '../../../redux/store';
import { checkPermissions } from '../../../helpers/checkPermissions';
import {
    read_dolores_seller_user,
    read_dolores_users
} from '../../../services/api/HumanResources';
import { alertMessage } from '../../../helpers/messagesAlerts';
import {
    assign_brand_seller,
    get_brand_filter_options,
    get_prospects,
    read_brand_row_with_seller,
    read_prospect_brand_rows,
    read_prospect_with_brand_update,
    search_brand_prospects
} from '../../../services/api/Prospects';
import { Toolbar_ } from '../../partials/navigation/Toolbar_.jsx';
import { Brands_Slider } from './components/Brands_Slider';
import Merge_Records from '../../partials/merge/Merge_Records';
import {
    Brands_Filters_Slider,
    defaultBrandFilters,
    read_active_brand_filters_count,
    read_brand_filters
} from './components/Brands_Filters_Slider';
import {
    read_seller_identity_keys,
    read_seller_member_by_sub_id,
    read_seller_name,
    read_seller_payload
} from '../../../utils/hr/seller_identity';
import { get_business_categories } from '../newLead/utils/business_categories';
import { Quest_Provider_Counts } from '../../../components/quest/Quest_Provider_Counts';
import { read_public_source_label } from '../../../utils/publicProductLabel';

const Brands = styled.div``;

const BrandsContent = styled.div`
    display: grid;
    gap: 2rem;
`;

const BrandsTools = styled.div`
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto auto;
    align-items: center;
    gap: 1rem;
`;

const BrandsActions = styled.div`
    display: flex;
    align-items: center;
    gap: 1rem;
    flex-wrap: wrap;
`;

const BrandsResultsCount = styled.div`
    min-height: 4rem;
    display: flex;
    align-items: center;
    gap: 0.8rem;
    padding: var(--smallPads) var(--normalPads);
    border-radius: var(--normalRadius);
    background: ${(props) => props.theme.low};
    box-shadow: ${(props) => props.theme.out};

    h5,
    h4 {
        color: ${(props) => props.theme.color};
        white-space: nowrap;
    }
`;

const BrandsEmpty = styled.div`
    padding: var(--normalPads);

    h5 {
        color: ${(props) => props.theme.color};
    }
`;

const BrandRow = styled(TR)`
    cursor: pointer;

    :hover {
        background: ${(props) => props.theme.low};
    }
`;

const BrandMergeSelectionCell = styled(TD)`
    width: 4rem;

    p {
        position: absolute;
        width: 1px;
        height: 1px;
        padding: 0;
        margin: -1px;
        overflow: hidden;
        clip: rect(0, 0, 0, 0);
        white-space: nowrap;
        border: 0;
    }
`;

const BrandsLoadMoreTrigger = styled.div`
    display: flex;
    justify-content: center;
    min-height: 0.1rem;
    padding: var(--normalPads);
`;

const BrandSourceTag = styled.div`
    display: inline-flex;
    padding: var(--smallPads);
    border-radius: var(--smallRadius);
    background: ${(props) => props.backgroundColor};

    h5 {
        color: ${(props) => props.theme.color};
        white-space: nowrap;
    }
`;

const prospectAssignedStatus = 'To Call';
const pageSize = 50;
const defaultBrandSortOrder = 'newest';
const brandBusinessCategories = get_business_categories((value) => value);
const brandCategoryOptions = Object.keys(brandBusinessCategories);
const brandSubcategoryOptions = [...new Set(Object.values(brandBusinessCategories).flat())].sort((first, second) => {
    return first.localeCompare(second);
});

/**
 * Applies client-status query parameters to the initial Brands filters.
 */
export const read_initial_brand_filters = (search = '') => {
    const searchParams = new URLSearchParams(search || '');
    const questClientStatuses = `${searchParams.get('questClientStatuses') || ''}`
        .split(',')
        .map((status) => status.trim().toLowerCase())
        .filter((status) => ['active', 'past'].includes(status));

    return read_brand_filters({
        ...defaultBrandFilters,
        questClientStatuses
    });
};

/**
 * Reads whether another complete Brand page can exist after this response.
 */
export const read_has_more_brand_pages = (page, totalRows, loadedPageRows) => {
    return Number(page || 0) * pageSize < Number(totalRows || 0) && Number(loadedPageRows || 0) > 0;
};

/**
 * Reads one displayable value for read-only brand details.
 */
const read_display_value = (value, fallback) => {
    return (Array.isArray(value) ? value : [value]).map((item) => `${item || ''}`.trim()).filter(Boolean).join(', ') || fallback;
};

/**
 * Generates the same stable pastel source color used by the Leads table.
 */
export const read_brand_source_background_color = (source) => {
    const seed = `${source || ''}`.split('').reduce((total, character, index) => {
        return total + character.charCodeAt(0) * (index + 1);
    }, 0);
    const hue = seed % 360;
    const saturation = 30 + seed % 10;
    const lightness = 88 + seed % 5;

    return `hsl(${hue} ${saturation}% ${lightness}%)`;
};

/**
 * Keeps internal application names out of the user-facing Brand source tag.
 */
export const read_brand_source_label = (source) => {
    const sourceLabel = read_display_value(source, '');

    return read_public_source_label(sourceLabel);
};

/**
 * Displays a Brand source as a color-coded tag while preserving the empty fallback.
 */
export const Brand_Source = ({ source, t }) => {
    const sourceLabel = read_brand_source_label(source);

    if (!sourceLabel) {
        return t('No source');
    }

    return (
        <BrandSourceTag backgroundColor={read_brand_source_background_color(sourceLabel)}>
            <h5>{sourceLabel}</h5>
        </BrandSourceTag>
    );
};

/**
 * Reads the derived Brand lifecycle status with an older-backend fallback.
 */
export const read_brand_last_status = (row) => {
    return row?.lastStatus || row?.prospectStatus || row?.status || 'Backlog';
};

/**
 * Builds the authenticated Doitforme member payload used by Assign to me.
 */
export const read_self_assignment_seller = (currentUser = {}) => {
    return read_seller_payload({
        ...currentUser,
        userId: currentUser?.userId || currentUser?.sub || '',
        id: currentUser?.userId || currentUser?.sub || ''
    });
};

/**
 * Reads whether the current member receives the all-member assignment dropdown.
 */
export const read_can_assign_any_seller = (currentUser = {}) => {
    return checkPermissions('King', currentUser?.roles);
};

/**
 * Reads the backend assignment error shown by the shared Alert_ component.
 */
export const read_brand_assignment_error_message = (result = {}, fallback = '') => {
    return result?.message || result?.msg || fallback;
};

export const brandTableHeaders = [
    'Seller',
    'Last status',
    'A/A',
    'Brand created',
    'Brand',
    'Client code',
    'First name',
    'Last name',
    'Source',
    'SASS',
    'Phone'
];

/**
 * Keeps backend-ranked Brand search results untouched.
 */
export const read_visible_brand_rows = (rows) => {
    return rows || [];
};

/**
 * Reads a sortable timestamp from saved lead and brand dates.
 */
const read_sort_time = (value) => {
    return new Date(value || 0).getTime() || 0;
};

/**
 * Reads the Brand record timestamp shown and sorted on the Brands page.
 */
const read_brand_created_at = (row) => {
    return row?.brandRecord?.legacyCreatedAt || row?.brandRecord?.createdAt || row?.createdAt || row?.legacyCreatedAt;
};

const read_brand_merge_id = (row) => `${row?.brandId || row?.id || ''}`;

/**
 * Starts a table merge review with the oldest selected Brand.
 */
export const sort_merge_brand_rows_oldest_first = (rows = []) => {
    const createdTime = (row) => {
        const timestamp = new Date(read_brand_created_at(row) || '').getTime();
        if (Number.isFinite(timestamp)) return timestamp;
        const id = read_brand_merge_id(row);
        return /^[a-f\d]{24}$/i.test(id) ? Number.parseInt(id.slice(0, 8), 16) * 1000 : Number.MAX_SAFE_INTEGER;
    };

    return [...rows].sort((first, second) => createdTime(first) - createdTime(second)
        || read_brand_merge_id(first).localeCompare(read_brand_merge_id(second)));
};

/**
 * Orders Brand rows by the same creation date displayed in the table.
 */
const sort_brand_rows_by_created = (rows, sortOrder = defaultBrandSortOrder) => {
    const direction = sortOrder === 'oldest' ? 1 : -1;

    return [...(rows || [])].sort((firstRow, secondRow) => {
        return (read_sort_time(read_brand_created_at(firstRow)) - read_sort_time(read_brand_created_at(secondRow))) * direction;
    });
};

/**
 * Keeps search rows in backend relevance order and normal rows in date order.
 */
export const read_ordered_brand_rows = (rows, searchValue, sortOrder = defaultBrandSortOrder) => {
    const normalizedSearch = `${searchValue || ''}`.trim();

    if (normalizedSearch) {
        return read_visible_brand_rows(rows, normalizedSearch);
    }

    return sort_brand_rows_by_created(rows, sortOrder);
};

// Keeps the four provider/status values in one compact table column.
export const Brand_Quest_Count_Cells = ({ row, t }) => (
    <TD><Quest_Provider_Counts brand={row} t={t} /></TD>
);

/**
 * Renders one row per brand across all prospects.
 */
export default function BRANDS() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const { lang } = useParams();
    const routeLanguage = lang || 'en';
    const { alerts, setAlerts } = useContext(AlertContext);
    const loadMoreTriggerRef = useRef(null);
    const loadingRequestRef = useRef(false);
    const loadingMoreRef = useRef(false);
    const requestSequenceRef = useRef(0);
    const [sidebar, setSidebar] = useState(true);
    const [prospects, setProspects] = useState([]);
    const [statusOptions, setStatusOptions] = useState([]);
    const [pageNo, setPageNo] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [search] = useDebounce(searchTerm, 1000);
    const [searchBrandRows, setSearchBrandRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);
    const [hasMoreBrandPages, setHasMoreBrandPages] = useState(false);
    const [totalBrandResults, setTotalBrandResults] = useState(0);
    const [assigningBrandId, setAssigningBrandId] = useState('');
    const [teamMembers, setTeamMembers] = useState([]);
    const [activeBrandRow, setActiveBrandRow] = useState(null);
    const [merging, setMerging] = useState(false);
    const [mergeMode, setMergeMode] = useState(false);
    const [selectedMergeBrandRows, setSelectedMergeBrandRows] = useState([]);
    const [mergeReviewRows, setMergeReviewRows] = useState([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [filtersOpen, setFiltersOpen] = useState(false);
    const [sortOrder, setSortOrder] = useState(defaultBrandSortOrder);
    const [sortOrderDraft, setSortOrderDraft] = useState(defaultBrandSortOrder);
    const initialBrandFiltersSearch = typeof window === 'undefined' ? '' : window.location.search;
    const [filters, setFilters] = useState(() => read_initial_brand_filters(initialBrandFiltersSearch));
    const [filterDraft, setFilterDraft] = useState(() => read_initial_brand_filters(initialBrandFiltersSearch));
    const loadedBrandRows = read_prospect_brand_rows(prospects);
    const brandRows = search ? searchBrandRows : loadedBrandRows;
    const visibleBrandRows = read_ordered_brand_rows(brandRows, search, sortOrder);
    const showLoadMore = hasMoreBrandPages && !search;
    const activeFiltersCount = read_active_brand_filters_count(filters) + Number(sortOrder !== defaultBrandSortOrder);
    const filtersButtonText = activeFiltersCount ? `${t('Filters')} (${activeFiltersCount})` : t('Filters');
    const currentUser = store.getState()?.user?.user || {};
    const currentSellerPayload = read_self_assignment_seller(currentUser);
    const canAssignToMe = read_seller_identity_keys(currentSellerPayload).length > 0;
    const canAssignAnySeller = read_can_assign_any_seller(currentUser);
    const canMerge = checkPermissions('King', currentUser.roles);

    const toggle_merge_mode = () => {
        setMergeMode((currentMode) => {
            if (currentMode) setSelectedMergeBrandRows([]);
            return !currentMode;
        });
    };

    const toggle_merge_brand = (row) => {
        const id = read_brand_merge_id(row);
        if (!id) return;
        setSelectedMergeBrandRows((currentRows) => currentRows.some((selected) => read_brand_merge_id(selected) === id)
            ? currentRows.filter((selected) => read_brand_merge_id(selected) !== id)
            : [...currentRows, row]);
    };

    const open_selected_merge = () => {
        if (selectedMergeBrandRows.length < 2) return;
        setMergeReviewRows(sort_merge_brand_rows_oldest_first(selectedMergeBrandRows));
    };

    const apply_selected_merge = () => {
        setMergeReviewRows([]);
        setSelectedMergeBrandRows([]);
        setMergeMode(false);
        setRefreshKey((value) => value + 1);
        alertMessage(alerts, setAlerts, t('Brands merged'), 'Success');
    };

    /**
     * Loads one page of prospects and keeps nested brands attached.
     */
    const load_prospects = (page, append) => {
        const requestId = requestSequenceRef.current + 1;

        requestSequenceRef.current = requestId;
        loadingRequestRef.current = true;
        setLoading(!append);

        if (append) {
            setLoadingMore(true);
        }

        return get_prospects(page, pageSize, {
            sortBy: 'brandCreatedAt',
            sortOrder,
            ...filters
        })
            .then((response) => {
                if (requestId !== requestSequenceRef.current) {
                    return;
                }

                const loadedProspects = response?.prospects || [];
                const loadedRows = read_prospect_brand_rows(loadedProspects);
                const loadedTotal = response?.count || 0;

                setProspects((currentProspects) => append ? [...currentProspects, ...loadedProspects] : loadedProspects);
                setSearchBrandRows([]);
                setTotalBrandResults(loadedTotal);
                setHasMoreBrandPages(read_has_more_brand_pages(page, loadedTotal, loadedRows.length));
                setPageNo(page + 1);
            })
            .catch(() => {
                if (requestId !== requestSequenceRef.current) {
                    return;
                }

                alertMessage(alerts, setAlerts, t('There was an error loading brands'), 'Error');
            })
            .finally(() => {
                if (requestId !== requestSequenceRef.current) {
                    return;
                }

                loadingRequestRef.current = false;
                setLoading(false);

                if (append) {
                    setLoadingMore(false);
                }
            });
    };

    /**
     * Loads the next sorted Brand page once the table bottom is visible.
     */
    const load_more_brands = () => {
        if (!showLoadMore || loadingRequestRef.current || loadingMoreRef.current) {
            return;
        }

        loadingMoreRef.current = true;

        load_prospects(pageNo, true)
            .finally(() => {
                loadingMoreRef.current = false;
                setLoadingMore(false);
            });
    };

    /**
     * Searches Brands from the database, matching Legacy search behavior.
     */
    const search_brands = (searchValue) => {
        const requestId = requestSequenceRef.current + 1;

        requestSequenceRef.current = requestId;
        loadingRequestRef.current = true;
        setLoading(true);

        search_brand_prospects(searchValue, filters)
            .then((response) => {
                if (requestId !== requestSequenceRef.current) {
                    return;
                }

                const loadedBrandRows = read_prospect_brand_rows(response?.prospects || []);
                const visibleRows = read_visible_brand_rows(loadedBrandRows, searchValue);

                setSearchBrandRows(visibleRows);
                setTotalBrandResults(response?.count || 0);
                setHasMoreBrandPages(false);
            })
            .catch(() => {
                if (requestId !== requestSequenceRef.current) {
                    return;
                }

                alertMessage(alerts, setAlerts, t('There was an error searching brands'), 'Error');
            })
            .finally(() => {
                if (requestId !== requestSequenceRef.current) {
                    return;
                }

                loadingRequestRef.current = false;
                setLoading(false);
            });
    };

    /**
     * Opens the Brands filters slider with the currently applied date order.
     */
    const open_filters_slider = () => {
        setActiveBrandRow(null);
        setSortOrderDraft(sortOrder);
        setFilterDraft(read_brand_filters(filters));
        setFiltersOpen(true);
    };

    /**
     * Applies the draft date order and reloads non-search Brand rows.
     */
    const apply_filters = () => {
        setSortOrder(sortOrderDraft === 'oldest' ? 'oldest' : defaultBrandSortOrder);
        setFilters(read_brand_filters(filterDraft));
        setFiltersOpen(false);
    };

    /**
     * Clears all Brand filters and restores newest-first sorting.
     */
    const clear_filters = () => {
        const emptyFilters = read_brand_filters(defaultBrandFilters);

        setSortOrderDraft(defaultBrandSortOrder);
        setSortOrder(defaultBrandSortOrder);
        setFilterDraft(emptyFilters);
        setFilters(emptyFilters);
        setFiltersOpen(false);
    };

    /**
     * Assigns an unowned Brand to one allowed Doitforme member and moves it to To Call.
     */
    const assign_seller = (row, sellerPayload) => {
        if (read_seller_identity_keys(sellerPayload).length === 0 || read_seller_identity_keys(row?.assignee).length > 0) {
            return;
        }

        setAssigningBrandId(row?.id);

        assign_brand_seller(row, sellerPayload, prospectAssignedStatus)
            .then((updatedProspect) => {
                if (updatedProspect?.blocked) {
                    alertMessage(
                        alerts,
                        setAlerts,
                        t(read_brand_assignment_error_message(updatedProspect, 'There was an error assigning seller')),
                        'Error'
                    );
                    return;
                }

                if (!updatedProspect) {
                    alertMessage(alerts, setAlerts, t('There was an error assigning seller'), 'Error');
                    return;
                }

                const canonicalProspect = read_prospect_with_brand_update(updatedProspect, row?.brandId, {
                    seller: sellerPayload,
                    status: prospectAssignedStatus
                });
                const updatedBrandRow = read_prospect_brand_rows([canonicalProspect]).find((brandRow) => {
                    return brandRow?.id === row?.id;
                }) || read_brand_row_with_seller(row, sellerPayload, prospectAssignedStatus);

                setProspects((currentProspects) => currentProspects.map((prospect) => {
                    if (prospect?._id !== updatedProspect?._id) {
                        return prospect;
                    }

                    return canonicalProspect;
                }));
                setActiveBrandRow(updatedBrandRow);
                setSearchBrandRows((currentRows) => currentRows.map((brandRow) => {
                    if (brandRow?.id !== row?.id) {
                        return brandRow;
                    }

                    return read_brand_row_with_seller(brandRow, sellerPayload, prospectAssignedStatus);
                }));
                alertMessage(alerts, setAlerts, t('Seller assigned'), 'Success');
            })
            .catch(() => {
                alertMessage(alerts, setAlerts, t('There was an error assigning seller'), 'Error');
            })
            .finally(() => {
                setAssigningBrandId('');
            });
    };

    /**
     * Assigns an unowned Brand to the logged-in regular member.
     */
    const assign_to_me = (row) => {
        if (!canAssignToMe) {
            return;
        }

        assign_seller(row, currentSellerPayload);
    };

    /**
     * Assigns an unowned Brand to the member selected by a King/Admin.
     */
    const assign_to_member = (row, sellerSubId) => {
        if (!canAssignAnySeller) {
            return;
        }

        const selectedSeller = read_seller_member_by_sub_id(teamMembers, sellerSubId);

        if (!selectedSeller) {
            return;
        }

        assign_seller(row, read_seller_payload(selectedSeller));
    };

    useEffect(() => {
        Promise.all([
            canAssignAnySeller
                ? read_dolores_users().then((users) => (users || []).map(read_dolores_seller_user)).catch(() => [])
                : Promise.resolve([]),
            get_brand_filter_options()
        ])
            .then(([members, filterOptions]) => {
                setTeamMembers(members || []);
                setStatusOptions(filterOptions?.statuses || []);
            })
            .catch(() => {
                alertMessage(alerts, setAlerts, t('There was an error loading brands'), 'Error');
            });
    }, []);

    useEffect(() => {
        if (search) {
            search_brands(search);
            return;
        }

        setSearchBrandRows([]);
        load_prospects(1, false);
    }, [search, sortOrder, filters, refreshKey]);

    useEffect(() => {
        const loadMoreTrigger = loadMoreTriggerRef.current;

        if (!loadMoreTrigger || !showLoadMore || loading || loadingMore) {
            return;
        }

        const observer = new IntersectionObserver((entries) => {
            if (entries[0]?.isIntersecting) {
                load_more_brands();
            }
        }, {
            root: null,
            rootMargin: '20% 0%',
            threshold: 0
        });

        observer.observe(loadMoreTrigger);

        return () => {
            observer.disconnect();
        };
    }, [showLoadMore, loading, loadingMore, pageNo, visibleBrandRows.length, sortOrder, filters]);

    return (
        <Brands>
            <Alert_ duration={6000} />
            <Toolbar_
                sidebar={sidebar}
                activeOption="brands"
                onSidebar={() => setSidebar(!sidebar)}
                routeLanguage={routeLanguage}
                onInternalNavigate={navigate}
            />
            {activeBrandRow && !merging ? (
                <Brands_Slider
                    t={t}
                    activeBrandRow={activeBrandRow}
                    assigningBrandId={assigningBrandId}
                    canAssignAnySeller={canAssignAnySeller}
                    canAssignToMe={canAssignToMe}
                    currentSeller={currentSellerPayload}
                    teamMembers={teamMembers}
                    readDisplayValue={read_display_value}
                    onAssignSeller={assign_to_member}
                    onAssignToMe={assign_to_me}
                    canMerge={canMerge}
                    onMerge={() => setMerging(true)}
                    onClose={() => setActiveBrandRow(null)}
                />
            ) : null}
            {activeBrandRow && merging && canMerge ? (
                <Merge_Records
                    entityType="brand"
                    record={{ id: activeBrandRow.brandId, label: activeBrandRow.brand }}
                    t={t}
                    onClose={() => setMerging(false)}
                    onMerged={result => {
                        setActiveBrandRow(read_prospect_brand_rows([result.prospect]).find(row => row.brandId === result.survivorId) || null);
                        setMerging(false);
                        setRefreshKey(value => value + 1);
                        alertMessage(alerts, setAlerts, t('Brands merged'), 'Success');
                    }}
                ></Merge_Records>
            ) : null}
            {mergeReviewRows.length >= 2 && canMerge ? (
                <Merge_Records
                    entityType="brand"
                    record={{ id: read_brand_merge_id(mergeReviewRows[0]), label: mergeReviewRows[0].brand }}
                    records={mergeReviewRows.map((row) => ({ id: read_brand_merge_id(row), label: row.brand }))}
                    t={t}
                    onClose={() => setMergeReviewRows([])}
                    onMerged={apply_selected_merge}
                />
            ) : null}
            {filtersOpen ? (
                <Brands_Filters_Slider
                    t={t}
                    sortOrder={sortOrderDraft}
                    onSortOrderChange={setSortOrderDraft}
                    filters={filterDraft}
                    appliedFilters={filters}
                    statusOptions={statusOptions}
                    categoryOptions={brandCategoryOptions}
                    subcategoryOptions={brandSubcategoryOptions}
                    onFiltersChange={setFilterDraft}
                    onApply={apply_filters}
                    onClear={clear_filters}
                    onClose={() => setFiltersOpen(false)}
                />
            ) : null}
            <PageSidebar>
                <PageAlwaysVisible>
                    <BrandsContent>
                        <PageTitle>
                            <Grey_Link iconLeft="Back" onClick={() => navigate(`/${routeLanguage}/`)} />
                            <h1>{t('Brands')}</h1>
                        </PageTitle>
                        <BrandsTools>
                            <Input_
                                placeholder={t('Search brands')}
                                value={searchTerm}
                                onChange={(event) => setSearchTerm(event.target.value)}
                            />
                            <BrandsActions>
                                {canMerge ? <Toggle_Stat active={mergeMode} text={t('Merge mode')} onClick={toggle_merge_mode} /> : null}
                                {canMerge && mergeMode && selectedMergeBrandRows.length >= 2 ? (
                                    <Main_Plain iconLeft="Group" text={t('Merge')} onClick={open_selected_merge} />
                                ) : null}
                                <Main_Plain iconLeft="Filter" text={filtersButtonText} onClick={open_filters_slider} />
                            </BrandsActions>
                            <BrandsResultsCount aria-live="polite" aria-label={t('Results')}>
                                <h5>{t('Results')}</h5>
                                <h4>{Math.max(Number(totalBrandResults || 0), 0).toLocaleString()}</h4>
                            </BrandsResultsCount>
                        </BrandsTools>

                        {loading ? <Partial_Loader text={t('Loading brands')} /> : null}

                        {!loading && visibleBrandRows.length === 0 ? (
                            <BrandsEmpty>
                                <h5>{t('No brands yet')}</h5>
                            </BrandsEmpty>
                        ) : null}

                        {visibleBrandRows.length > 0 ? (
                            <Table>
                                <TR>
                                    {canMerge && mergeMode ? <TH title={t('Select brands')}></TH> : null}
                                    {brandTableHeaders.map((header) => <TH key={header}>{t(header)}</TH>)}
                                </TR>
                                {visibleBrandRows.map((row, index) => (
                                    <BrandRow key={row.id} onClick={() => setActiveBrandRow(row)}>
                                        {canMerge && mergeMode ? (
                                            <BrandMergeSelectionCell onClick={(event) => event.stopPropagation()}>
                                                <Checkbox_
                                                    checked={selectedMergeBrandRows.some((selected) => read_brand_merge_id(selected) === read_brand_merge_id(row))}
                                                    id={`merge-brand-${read_brand_merge_id(row)}`}
                                                    text={`${t('Select brand')} ${row.brand}`}
                                                    title={`${t('Select brand')} ${row.brand}`}
                                                    onChange={() => {}}
                                                    onClick={() => toggle_merge_brand(row)}
                                                />
                                            </BrandMergeSelectionCell>
                                        ) : null}
                                        <TD>
                                            <h5>{read_display_value(read_seller_name(row?.assignee), t('No seller'))}</h5>
                                        </TD>
                                        <TD>{t(read_brand_last_status(row))}</TD>
                                        <TD>{index + 1}</TD>
                                        <TD>{new Date(read_brand_created_at(row)).toLocaleDateString('el-GR')}</TD>
                                        <TD>{row.brand}</TD>
                                        <TD>{read_display_value(row?.clientCode, t('No client code'))}</TD>
                                        <TD>{read_display_value(row?.firstname, '')}</TD>
                                        <TD>{read_display_value(row?.lastname, '')}</TD>
                                        <TD><Brand_Source source={row?.source} t={t} /></TD>
                                        <Brand_Quest_Count_Cells row={row} t={t} />
                                        <TD>{read_display_value(row?.phones, '')}</TD>
                                    </BrandRow>
                                ))}
                            </Table>
                        ) : null}

                        {showLoadMore ? (
                            <BrandsLoadMoreTrigger ref={loadMoreTriggerRef}>
                                {loadingMore ? <Partial_Loader text={t('Loading brands')} /> : null}
                            </BrandsLoadMoreTrigger>
                        ) : null}
                    </BrandsContent>
                </PageAlwaysVisible>
            </PageSidebar>
        </Brands>
    );
}

export { BRANDS };
